import axios from "axios";
import authAxios from "../axios";
import { ServerResponse } from "../axios";
import { BASE_URL } from "@src/utils";

// 이메일 중복 확인
export const checkEmailAvailability = (email: string) => {
  return axios.get<ServerResponse>(
    `${BASE_URL}/v3/20240404/customer/check/id?loginId=${encodeURIComponent(email)}`,
  );
};

// 비밀번호 초기화 요청
export const requestPasswordReset = (email: string) => {
  return axios.get<ServerResponse>(
    `${BASE_URL}/v3/20240404/customer/find/loginPwd?loginId=${encodeURIComponent(email)}`,
  );
};

// 초대 코드 유효성 검사
export const validateReferralCode = (referralCode: string) => {
  return authAxios.get<ServerResponse>(
    `${BASE_URL}/v3/20240404/customer/invitationCode/${referralCode}/available`,
  );
};

// 닉네임 중복 확인
export const checkNicknameAvailability = (nickname: string) => {
  return authAxios.get<ServerResponse>(
    `${BASE_URL}/v3/20240404/customer/nick/available?nick=${encodeURIComponent(nickname)}`,
  );
};

// 전화번호 인증 코드 요청
export const requestPhoneVerificationCode = (
  tel: string,
  type: { type: string; data?: string },
) => {
  const data = type.type !== "default" ? { tel: tel, type: type.type } : { tel: tel };
  if (type.data) {
    data["k"] = type.data;
  }
  return axios.post<ServerResponse>(
    `${BASE_URL}/v3/20240404/auth/call?${new URLSearchParams(data).toString()}`,
    data,
  );
};

// 전화번호 인증 코드 확인
export const verifyPhoneVerificationCode = (
  tel: string,
  token: string,
  type: { type: string; data?: string },
) => {
  const data = {
    tel: tel,
    token: token,
  };
  let response: any;
  if (type.type === "findLoginId") {
    response = axios.get<ServerResponse>(
      `${BASE_URL}/v3/20240404/customer/find/loginId?${new URLSearchParams(data).toString()}`,
    );
  } else if (type.type === "changeTel") {
    response = authAxios.put<ServerResponse>(
      `${BASE_URL}/v3/20241108/customer/tel?${new URLSearchParams(data).toString()}`,
      data,
    );
  } else if (type.type === "default") {
    response = axios.put<ServerResponse>(
      `${BASE_URL}/v3/20240404/auth/confirm?${new URLSearchParams(data).toString()}`,
      data,
    );
  }
  return response;
};

// 로그인
export const login = (data: { loginId: string; pwd: string }) => {
  return axios.post<ServerResponse>(
    `${BASE_URL}/v3/20240404/customer/login?${new URLSearchParams(data).toString()}`,
    data,
  );
};

// 로그인 토큰 <-> 액세스 토큰 교환
export const exchangeLoginToken = (token: string) => {
  const data = {
    loginToken: token,
  };
  return axios.post<ServerResponse>(`${BASE_URL}/v3/jwt/:loginToken?loginToken=${token}`, data);
};

// 로그아웃
export const logout = (refreshToken: string) => {
  const url = `${BASE_URL}/v3/20240404/customer/logout?refreshToken=${refreshToken}`;
  const data = {
    refreshToken: refreshToken,
  };
  return authAxios.post<ServerResponse>(url, data);
};

// 회원 가입 / 수정
export const submitUserInfo = (data: { [key: string]: string }, type?: "signUp" | "modify") => {
  const url = `${BASE_URL}/v3/20240404/customer?${new URLSearchParams(data).toString()}`;
  if (type === "modify") {
    return authAxios.put<
      ServerResponse<{
        accessToken: string;
        refreshToken: string;
      }>
    >(url, data);
  } else {
    return axios.post<
      ServerResponse<{
        accessToken: string;
        refreshToken: string;
      }>
    >(url, data);
  }
};

// 추가 정보 조회
export const getUserAdditionalInfo = () => {
  return authAxios.get<ServerResponse>(`${BASE_URL}/v3/20240404/customer`);
};

// 닉네임 변경
export const changeNickname = (nick: string) => {
  return authAxios.put<ServerResponse>(`${BASE_URL}/v3/20241108/customer/nick`, { nick: nick });
};

// 비밀번호 변경
export const changePassword = (password: string, newPassword: string) => {
  return authAxios.put<ServerResponse>(`${BASE_URL}/v3/20241108/customer/loginPwd`, {
    pwd: password,
    newPwd: newPassword,
  });
};

// 프로필용으로 이미지 업로드
export const uploadImgTypeProfile = (file: File) => {
  const url = `${BASE_URL}/v4/uploadImageFile`;
  const formData = new FormData();
  formData.append("file", file); // 이미지 파일 추가
  formData.append("width", "300"); // 너비 설정
  formData.append("type", "profile"); // 타입 설정
  return authAxios.post<ServerResponse>(url, formData);
};

// 프로필 이미지 URL 변경
export const changeProfileImg = (imgUrl: string) => {
  return authAxios.put<ServerResponse>(`${BASE_URL}/v3/20241108/customer/imageUrl`, {
    imageUrl: imgUrl,
  });
};

// 가입 경로 조회
export const getInpath = () => {
  return authAxios.get<ServerResponse>(`${BASE_URL}/v3/20240404/customer/inpath`);
};

// 배우자 정보 변경
export const changeMate = ({ mateName, mateTel }: { mateName: string; mateTel: string }) => {
  const data = {
    mateName: mateName,
    mateTel: mateTel,
  };

  return authAxios.put<ServerResponse>(`${BASE_URL}/v4/20250407/customer/mate`, data);
};

// 가입 3단계 추가 정보 등록
export const changeAdditionalInfo = ({
  nick,
  weddingTsp,
  region,
  invitationCode,
}: {
  nick: string;
  weddingTsp: string;
  region: string;
  invitationCode: string;
}) => {
  const data = {
    nick,
    weddingTsp,
    region,
    invitationCode,
  };

  return authAxios.put<ServerResponse>(`${BASE_URL}/v4/20250407/customer/additional-info`, data);
};

// 홈화면 예식정보입력
export const changeWeddingInfo = ({
  weddingTsp,
  region,
  weddinghallName,
  weddinghallUuid,
}: {
  weddingTsp: string;
  region: string;
  weddinghallName: string;
  weddinghallUuid: string;
}) => {
  const data = {
    weddingTsp,
    region,
    weddinghallName,
    weddinghallUuid,
  };

  return authAxios.put<ServerResponse>(`${BASE_URL}/v4/20250407/customer/wedding-info`, data);
};
